import { useMemo } from 'react';
import type { MenuBuilderComponentDto, MenuItemVariantDto } from '../../generated-api';
import { useAllProducts } from './useAllProducts';

export interface ResolvedComponent {
  component: MenuItemVariantDto;
  componentDto: MenuBuilderComponentDto;
}

/**
 * Hook to resolve component references to full MenuItemVariantDto objects
 * Looks up components in both menu sections and component products
 *
 * @param item - The composite product item with components
 * @returns Array of resolved components with their DTOs
 */
export function useResolvedComponents(item: MenuItemVariantDto | undefined): ResolvedComponent[] {
  const allProducts = useAllProducts();

  return useMemo(() => {
    if (!item || !item.components || item.components.length === 0) {
      return [];
    }

    return item.components
      .map((comp) => {
        // Component is now locked to a specific productSizeId (variant)
        const fullComponent = allProducts.find((p) => p.variantLinkId === comp.productSizeId);
        return fullComponent ? { component: fullComponent, componentDto: comp } : null;
      })
      .filter((c): c is ResolvedComponent => c !== null);
  }, [item, allProducts]);
}

/**
 * Recursively checks whether any component (at any depth) has option groups.
 * Used to determine if the composite item modal should open.
 */
export function hasDeepCustomizableComponents(
  resolvedComponents: ResolvedComponent[],
  allProducts: MenuItemVariantDto[]
): boolean {
  for (const rc of resolvedComponents) {
    if (rc.component.optionGroups.length > 0) {
      return true;
    }
    if (rc.component.isComposite && rc.component.components) {
      const subResolved = rc.component.components
        .map((sub) => {
          const full = allProducts.find((p) => p.variantLinkId === sub.productSizeId);
          return full ? { component: full, componentDto: sub } : null;
        })
        .filter((c): c is ResolvedComponent => c !== null);
      if (hasDeepCustomizableComponents(subResolved, allProducts)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Recursively collects all components that have option groups, at any depth.
 * Returns them in order for use as customization steps.
 */
export function collectCustomizableComponents(
  resolvedComponents: ResolvedComponent[],
  allProducts: MenuItemVariantDto[]
): ResolvedComponent[] {
  const result: ResolvedComponent[] = [];
  for (const rc of resolvedComponents) {
    if (rc.component.optionGroups.length > 0) {
      result.push(rc);
    }
    if (rc.component.isComposite && rc.component.components) {
      const subResolved = rc.component.components
        .map((sub) => {
          const full = allProducts.find((p) => p.variantLinkId === sub.productSizeId);
          return full ? { component: full, componentDto: sub } : null;
        })
        .filter((c): c is ResolvedComponent => c !== null);
      result.push(...collectCustomizableComponents(subResolved, allProducts));
    }
  }
  return result;
}
